/**
 * Standard and common reduced VAT/GST rates by country, with the display currency. Originally compiled from memory of the
 * European Commission "VAT rates applied in the Member States" table and national tax authorities.
 * Spot-check of 31 Oct 2026 (corrections applied in this version):
 *  - Row-level "checked" (31 Oct 2026, per the owner-supplied verified values): Bulgaria and India.
 *  - Verified on official pages: Estonia (EMTA), Austria 4.9% food rate (USP.gv.at), Bulgaria euro adoption (EC Access2Markets),
 *    Ukraine (PwC Worldwide Tax Summaries, a professional-services source, not the tax authority).
 *  - Verified on secondary sources only (Eurofiscalis, VATupdate, fiscal-requirements.com, KPMG TaxNewsFlash, BDO, taxtips.ca,
 *    taxguru.in, DDTC): Finland, Lithuania, Romania, Cyprus, Nova Scotia, India, Thailand, Indonesia and every row not listed above.
 *    Not yet compared with the EC PDF, CRA, ESTV or ANAF pages.
 * The page shows the "as of" date and tells people to confirm. Review each January.
 */
export const VAT_RATES_AS_OF = "October 2026";

export interface VatCountry { country: string; rate: number; reduced?: number[]; currency: string; label?: string; note?: string; /** Set only when the row was checked against the cited sources on that date (ISO). */ checked?: { on: string; basis: string } }

export const VAT_COUNTRIES: VatCountry[] = [
  // European Union
  { country: "Austria", rate: 20, reduced: [13, 10, 4.9], currency: "EUR", note: "4.9% applies to a closed list of basic foods from 1 Jul 2026." },
  { country: "Belgium", rate: 21, reduced: [12, 6], currency: "EUR" },
  { country: "Bulgaria", rate: 20, reduced: [9], currency: "EUR", note: "Bulgaria adopted the euro on 1 Jan 2026. Reduced 9% applies to accommodation, books, periodicals and baby items; restaurant services returned to 20% on 1 Jan 2025.", checked: { on: "2026-10-31", basis: "standard 20%, reduced 9% (accommodation, books, periodicals, baby items), restaurants back to 20% since 1 Jan 2025, euro since 1 Jan 2026" } },
  { country: "Croatia", rate: 25, reduced: [13, 5], currency: "EUR" },
  { country: "Cyprus", rate: 19, reduced: [9, 5, 3], currency: "EUR" },
  { country: "Czechia", rate: 21, reduced: [12], currency: "CZK" },
  { country: "Denmark", rate: 25, currency: "DKK" },
  { country: "Estonia", rate: 24, reduced: [13, 9], currency: "EUR" },
  { country: "Finland", rate: 25.5, reduced: [13.5, 10], currency: "EUR" },
  { country: "France", rate: 20, reduced: [10, 5.5, 2.1], currency: "EUR" },
  { country: "Germany", rate: 19, reduced: [7], currency: "EUR" },
  { country: "Greece", rate: 24, reduced: [13, 6], currency: "EUR" },
  { country: "Hungary", rate: 27, reduced: [18, 5], currency: "HUF" },
  { country: "Ireland", rate: 23, reduced: [13.5, 9, 4.8], currency: "EUR" },
  { country: "Italy", rate: 22, reduced: [10, 5, 4], currency: "EUR" },
  { country: "Latvia", rate: 21, reduced: [12, 5], currency: "EUR" },
  { country: "Lithuania", rate: 21, reduced: [12, 5], currency: "EUR" },
  { country: "Luxembourg", rate: 17, reduced: [14, 8, 3], currency: "EUR" },
  { country: "Malta", rate: 18, reduced: [7, 5], currency: "EUR" },
  { country: "Netherlands", rate: 21, reduced: [9], currency: "EUR" },
  { country: "Poland", rate: 23, reduced: [8, 5], currency: "PLN" },
  { country: "Portugal", rate: 23, reduced: [13, 6], currency: "EUR" },
  { country: "Romania", rate: 21, reduced: [11], currency: "RON" },
  { country: "Slovakia", rate: 23, reduced: [19, 5], currency: "EUR" },
  { country: "Slovenia", rate: 22, reduced: [9.5, 5], currency: "EUR" },
  { country: "Spain", rate: 21, reduced: [10, 4], currency: "EUR" },
  { country: "Sweden", rate: 25, reduced: [12, 6], currency: "SEK" },
  // Rest of Europe
  { country: "United Kingdom", rate: 20, reduced: [5], currency: "GBP" },
  { country: "Norway", rate: 25, reduced: [15, 12], currency: "NOK" },
  { country: "Switzerland", rate: 8.1, reduced: [3.8, 2.6], currency: "CHF" },
  { country: "Iceland", rate: 24, reduced: [11], currency: "ISK" },
  { country: "Turkey", rate: 20, reduced: [10, 1], currency: "TRY" },
  { country: "Ukraine", rate: 20, reduced: [14, 7], currency: "UAH", note: "14% applies to certain plant agricultural products." },
  // Canada
  { country: "Canada — GST only (AB, YT, NT, NU)", rate: 5, currency: "CAD", label: "Canada GST" },
  { country: "Canada — Ontario HST", rate: 13, currency: "CAD", label: "Canada HST" },
  { country: "Canada — Nova Scotia HST", rate: 14, currency: "CAD", label: "Canada HST", note: "Cut from 15% to 14% on 1 Apr 2025." },
  { country: "Canada — New Brunswick, Newfoundland, PEI HST", rate: 15, currency: "CAD", label: "Canada HST" },
  { country: "Canada — British Columbia (GST + PST)", rate: 12, currency: "CAD", label: "Canada GST+PST" },
  { country: "Canada — Manitoba (GST + RST)", rate: 12, currency: "CAD", label: "Canada GST+RST" },
  { country: "Canada — Saskatchewan (GST + PST)", rate: 11, currency: "CAD", label: "Canada GST+PST" },
  { country: "Canada — Quebec (GST + QST)", rate: 14.975, currency: "CAD", label: "Canada GST+QST" },
  // Asia-Pacific
  { country: "Australia (GST)", rate: 10, currency: "AUD" },
  { country: "New Zealand (GST)", rate: 15, currency: "NZD" },
  { country: "Singapore (GST)", rate: 9, currency: "SGD" },
  { country: "Japan", rate: 10, reduced: [8], currency: "JPY" },
  { country: "South Korea", rate: 10, currency: "KRW" },
  { country: "Thailand", rate: 7, currency: "THB", note: "The statutory rate is 10%; the reduced 7% is extended to 30 Sep 2027." },
  { country: "India (GST)", rate: 18, reduced: [5], currency: "INR", note: "From 22 Sep 2025 the slabs are 5%, 18% and 40% (sin and luxury goods). Special rates remain: 0.25% (rough diamonds), 1.5% (cut diamonds), 3% (gold and silver) and 28% (tobacco and pan masala only); the 12% slab and the 28% slab for other goods were abolished.", checked: { on: "2026-10-31", basis: "slabs 5/18/40 plus special rates 0.25, 1.5, 3 and 28 (tobacco and pan masala)" } },
  { country: "Philippines", rate: 12, currency: "PHP" },
  { country: "Indonesia", rate: 11, currency: "IDR", note: "Effective rate 11%; the statutory 12% applies only to luxury goods." },
  // Middle East and Africa
  { country: "UAE", rate: 5, currency: "AED" },
  { country: "Saudi Arabia", rate: 15, currency: "SAR" },
  { country: "Israel", rate: 18, currency: "ILS" },
  { country: "South Africa", rate: 15, currency: "ZAR" },
  { country: "Kenya", rate: 16, currency: "KES" },
  { country: "Nigeria", rate: 7.5, currency: "NGN" },
  { country: "Egypt", rate: 14, currency: "EGP" },
  // Americas
  { country: "Mexico (IVA)", rate: 16, currency: "MXN" },
  { country: "Chile (IVA)", rate: 19, currency: "CLP" },
  { country: "Colombia (IVA)", rate: 19, reduced: [5], currency: "COP" },
  { country: "Argentina (IVA)", rate: 21, reduced: [10.5], currency: "ARS" },
];

export const NO_CURRENCY = "";

/** Formats an amount with the currency's own symbol and decimals; falls back to a plain 2-decimal number. */
export function money(n: number, currency: string): string {
  if (!currency) return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(n);
  } catch {
    return n.toFixed(2);
  }
}

export interface VatResult { net: number; vat: number; gross: number }
export function vatCalc(amount: number, ratePct: number, inclusive: boolean): VatResult {
  if (inclusive) { const net = amount / (1 + ratePct / 100); return { net, vat: amount - net, gross: amount }; }
  const vat = (amount * ratePct) / 100;
  return { net: amount, vat, gross: amount + vat };
}
